// The status type comes from the Prisma client, never from `@dealstack/shared/lib/generated`.
// This module sits in the backend boot graph, and the generated barrel is produced *by* booting
// it (generate-swagger -> generate-openapi), so depending on it is a cycle: every build that
// type-checks before generation fails to resolve it. Prisma's copy is generated from the schema.
import type { DocumentWorkflowInstanceStatus } from '@dealstack/prisma-client';

// InvestorSigningStatus lives in its own leaf module (no generated-type dependency) so that
// backend boot-graph code can import it without triggering a runtime require of the generated
// barrel, which does not exist yet at swagger-generation time. Re-exported here for convenience.
export { InvestorSigningStatus } from '@dealstack/shared/lib/common/business/investorSigningStatus';

export enum DisplayWorkflowStatus {
	COMPLETED = 'COMPLETED',
	IN_PROGRESS = 'IN_PROGRESS',
	AWAITING_DATE = 'AWAITING_DATE',
	REVOKED = 'REVOKED',
	ERROR = 'ERROR',
}

/** The two DC-1000 columns the awaiting-date state is derived from. */
export interface PostDatingState {
	requiresPostDating: boolean;
	/** `Date` from the backend, ISO string from the generated API models. */
	dateAppliedAt: Date | string | null;
}

/**
 * Signing has closed but the counter-signer still owes the effective date. Callers without the
 * post-dating fields get `false` so they keep today's behaviour rather than mislabelling.
 *
 * `status` is the string-literal union of the enum rather than the enum type itself, so both the
 * Prisma-generated and Swagger-generated `DocumentWorkflowInstanceStatus` enums satisfy it
 * directly — they share the same string values but are otherwise nominally incompatible types.
 */
export const isAwaitingPostDatingDate = (
	status: `${DocumentWorkflowInstanceStatus}`,
	postDating?: PostDatingState | null
): boolean =>
	status === 'COMPLETED' &&
	postDating?.requiresPostDating === true &&
	postDating.dateAppliedAt === null;

export const mapToDisplayStatus = (
	status: `${DocumentWorkflowInstanceStatus}`,
	postDating?: PostDatingState | null
): DisplayWorkflowStatus => {
	if (isAwaitingPostDatingDate(status, postDating)) {
		return DisplayWorkflowStatus.AWAITING_DATE;
	}
	switch (status) {
		case 'COMPLETED':
			return DisplayWorkflowStatus.COMPLETED;
		case 'CANCELLED':
			return DisplayWorkflowStatus.REVOKED;
		case 'ERROR':
		case 'EXPIRED':
			return DisplayWorkflowStatus.ERROR;
		case 'PENDING':
		case 'IN_PROGRESS':
		case 'PENDING_ADDITIONAL_SIGNATURES':
		case 'PENDING_FORM_SUBMISSION':
		case 'REJECTED':
			return DisplayWorkflowStatus.IN_PROGRESS;
	}
};

/** Plain-English label used by workers that do not have access to i18n (e.g. Excel export). */
export const getDisplayWorkflowStatusLabel = (status: DisplayWorkflowStatus): string => {
	switch (status) {
		case DisplayWorkflowStatus.COMPLETED:
			return 'Completed';
		case DisplayWorkflowStatus.IN_PROGRESS:
			return 'In Progress';
		case DisplayWorkflowStatus.AWAITING_DATE:
			return 'Awaiting date';
		case DisplayWorkflowStatus.REVOKED:
			return 'Revoked';
		case DisplayWorkflowStatus.ERROR:
			return 'Error';
	}
};
